import { Component, inject, OnInit, signal } from '@angular/core';
import { ServicePortalApi } from '../../api/api';
import { CustomerAccount } from '../../api/types';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  SynAlertComponent,
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent,
  SynButtonComponent,
  SynIconComponent,
} from '@synergy-design-system/angular';
import { ColumnDescription, TableComponent } from '../components/table';
import { Location } from '@angular/common';
import { getUserData } from '../../api/auth';

@Component({
  selector: 'app-customer-manager',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/customers" i18n="@@customer-accounts">Customer Accounts</syn-breadcrumb-item>
    </syn-breadcrumb>

    <div class="flex-row gap-1 space-between">
      <h1 i18n="@@customer-accounts">Customer Accounts</h1>
      <div class="flex-row gap-1 margin-vert-center">
        <syn-button size="medium" routerLink="./export" [disabled]="!hasAccess()">
          <syn-icon name="save_fill" slot="prefix" />
          <span i18n="@@export-customer-accounts">Export Customer Accounts</span>
        </syn-button>
        <syn-button size="medium" routerLink="./new" [disabled]="!hasAccess()">
          <syn-icon name="add_fill" slot="prefix" />
          <span i18n="@@create-new-customer-account">Create a New Customer Account</span>
        </syn-button>
      </div>
    </div>

    @if (hasAccess()) {
      <syn-alert variant="primary" [open]="true" class="margin-bottom-1">
      <span i18n="@@customer-search-explanation">
        You can search for an email address, first or last name.
        Press Enter to start the search.
      </span>
        <syn-icon slot="icon" name="info_fill" />
      </syn-alert>
    } @else {
      <syn-alert variant="danger" [open]="true" class="margin-bottom-1">
        <span i18n="@@error-no-customer-access">
          You do not have access to the customer manager. You can request access via the following link:
        </span>&nbsp;<a href="https://userservice.sick.com/saw/ess/offeringPage/12118463">SMAX</a>
        <syn-icon slot="icon" name="info_fill" />
      </syn-alert>
    }

    <app-table
      [columns]="columns"
      [values]="accounts()"
      [pageSize]="20"
      [clickable]="true"
      (selectValue)="openAccount($event)"
      [customSearch]="true"
      [loading]="loading()"
      [disabled]="!hasAccess()"
      (searchEvent)="onSearch($event)"
      [(searchTextField)]="query"
    />
  `,
  styles: [
    `
    `,
  ],
  imports: [
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    SynButtonComponent,
    SynIconComponent,
    TableComponent,
    SynAlertComponent,
  ],
})
export class CustomerManagerComponent implements OnInit {
  api = inject(ServicePortalApi);
  router = inject(Router);
  activeRoute = inject(ActivatedRoute);
  location = inject(Location);

  accounts = signal<CustomerAccount[]>([]);
  loading = signal(false);
  query = signal('');
  hasAccess = signal(false);

  columns: ColumnDescription<CustomerAccount>[] = [
    {
      label: $localize`:@@username:Username`,
      getField: (account) => account.username,
      style: 'width: 25%',
    },
    {
      label: $localize`:@@email:Email`,
      getField: (account) => account.email ?? '',
      style: 'width: 25%',
    },
    {
      label: $localize`:@@first-name:First Name`,
      getField: (account) => account.firstName,
      style: 'width: 20%',
    },
    {
      label: $localize`:@@last-name:Last Name`,
      getField: (account) => account.lastName,
      style: 'width: 20%',
    },
    {
      label: $localize`:@@enabled:Enabled`,
      getField: (account) => account.enabled ? '✔' : '✖',
      style: 'width: 5%',
    },
    {
      label: $localize`:@@email-verified:Email Verified`,
      getField: (account) => account.emailVerified ? '✔' : '✖',
      style: 'width: 5%',
    },
  ];

  ngOnInit(): void {
    const query = this.activeRoute.snapshot.queryParams['query'];
    if (query) {
      this.query.set(query);
      this.onSearch(query);
    }

    const roles = getUserData().roles;
    const isManager = roles.includes('sickid_usermanager');
    const isAdmin = roles.includes('sickid_usermanager_admin');
    this.hasAccess.set(isManager || isAdmin);
  }

  onSearch(query: string) {
    if (query.trim().length === 0) {
      this.accounts.set([]);
      return;
    }

    this.loading.set(true);
    this.api.searchCustomers(query, (result) => {
      this.accounts.set(result.accounts);
      this.loading.set(false);

      const url = this.router.createUrlTree(['customers'], { queryParams: { query } });
      this.location.go(url.toString());
    });
  }

  openAccount(account: CustomerAccount) {
    void this.router.navigate(['customers', account.id]);
  }
}
